import client from './client';
import {
  CompanyPresentationsResponse,
  CompanyProductsResponse,
  CoveragePresentationPage,
  MilestoneDashboardOverview,
  MilestoneDimension,
  MilestoneDimensionStats,
  MilestoneEntityDetail,
  MilestoneEntityDimension,
} from '../types/milestone';

export const getMilestoneOverview = async (
  days?: number,
): Promise<MilestoneDashboardOverview> => {
  const response = await client.get('/api/v1/milestones/stats/overview', {
    params: days != null ? { days } : undefined,
  });
  return response.data;
};

export const getMilestoneStatsByDimension = async (
  dimension: MilestoneDimension,
  days?: number,
): Promise<MilestoneDimensionStats> => {
  const response = await client.get(`/api/v1/milestones/stats/by/${dimension}`, {
    params: days != null ? { days } : undefined,
  });
  return response.data;
};

export const getEntityMilestones = async (
  dimension: MilestoneEntityDimension,
  entityId: string,
  days?: number,
): Promise<MilestoneEntityDetail> => {
  const response = await client.get(
    `/api/v1/milestones/by/${dimension}/${encodeURIComponent(entityId)}`,
    { params: days != null ? { days } : undefined },
  );
  return response.data;
};

export const getCompanyProducts = async (
  companyId: string,
): Promise<CompanyProductsResponse> => {
  const response = await client.get(
    `/api/v1/milestones/company/${encodeURIComponent(companyId)}/products`,
  );
  return response.data;
};

export const getCompanyPresentations = async (
  companyId: string,
): Promise<CompanyPresentationsResponse> => {
  const response = await client.get(
    `/api/v1/milestones/company/${encodeURIComponent(companyId)}/presentations`,
  );
  return response.data;
};

// Reuse the existing per-entity search endpoints to look up a specific
// company / technology / disease by name for the milestone dashboard.
const SEARCH_PATH: Record<MilestoneDimension, string> = {
  company: '/api/v1/companies/search',
  technology: '/api/v1/technologies/search',
  disease: '/api/v1/diseases/search',
};

export interface EntitySearchResult {
  id: string;
  name: string;
}

export const getCoveragePresentations = async (
  hasMilestones: boolean,
  opts: { days?: number; skip?: number; limit?: number } = {},
): Promise<CoveragePresentationPage> => {
  const response = await client.get('/api/v1/milestones/coverage/presentations', {
    params: {
      has_milestones: hasMilestones,
      days: opts.days,
      skip: opts.skip ?? 0,
      limit: opts.limit ?? 25,
    },
  });
  return response.data;
};

export const searchEntities = async (
  dimension: MilestoneDimension,
  q: string,
  limit = 20,
): Promise<EntitySearchResult[]> => {
  const response = await client.get(SEARCH_PATH[dimension], {
    params: { q, limit },
  });
  // All three endpoints return SearchResult: { results, count, query }.
  return (response.data?.results ?? []).map((r: EntitySearchResult) => ({
    id: r.id,
    name: r.name,
  }));
};
